export function isString(str: unknown): str is string {
  return Boolean(typeof str === 'string' || str instanceof String);
}

export function isNonEmptyString(str: unknown): str is string {
  return isString(str) && str.trim().length > 0;
}

export function capitalize(str: string): string {
  if (!str) return str;
  return str.charAt(0).toUpperCase() + str.slice(1);
}

/**
 * Converts a camelCase string to kebab-case.
 *
 * @param str - The camelCase string to convert
 * @returns The kebab-case string
 *
 * @example
 * toKebabCase('backgroundColor') // 'background-color'
 * toKebabCase('fontSize') // 'font-size'
 * toKebabCase('width') // 'width'
 */
export function toKebabCase(str: string): string {
  return str.replace(/[A-Z]/g, (match) => `-${match.toLowerCase()}`);
}

/**
 * Extracts the language code from an ISO locale string (e.g., "en" from "en-US")
 */
export function getLocaleCode(locale: string): string {
  return locale.split('-')[0]!;
}

/**
 * Returns the initials from a full name string.
 * Uses the first character of the first word and the first character of the last word.
 *
 * @example getInitials('John Smith') // 'JS'
 * @example getInitials('John') // 'J'
 */
export function getInitials(fullName: string): string {
  const parts = fullName.trim().split(/\s+/);
  const first = parts[0]?.[0] ?? '';
  const last = parts.length > 1 ? (parts[parts.length - 1]?.[0] ?? '') : '';
  return (first + last).toUpperCase();
}

const SUPERSCRIPT_DIGITS = '⁰¹²³⁴⁵⁶⁷⁸⁹';

/** Converts a non-negative integer to Unicode superscript digits, e.g. `12` → `'¹²'`. */
export function toSuperscript(value: number): string {
  return Array.from(String(value), digit => SUPERSCRIPT_DIGITS[Number(digit)]).join('');
}
